import { type IntegrationOptions } from '@/atoms/integrationAtoms';
import { bootOrUpdate, configureIntercomLocale, IntercomId } from '@/lib/Intercom';
import { getAppType } from '@/lib/analytics/legacy-sender-context';
import { type EHRProfileResponse, type UserEHRLaunchProfile } from '@/types';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { isIntercomDisabledForLocale } from '@/utils/intercomLocale';
import { type UserBillingInfo } from '@heidi/commerce/contracts';
import { type Region } from '@heidi/schema/ml-scribe';
import {
  type JoinedWorkspaceRelation,
  type TeamResponse,
  type UserResponse,
} from '@heidi/workspace/contracts';
import type * as IntercomTypes from '@intercom/messenger-js-sdk/dist/types';

// Support routes Fin conversations on these exact strings, so a value here
// must not change without Intercom's routing changing with it.
export type HcpVerificationIntercomStatus =
  | 'unverified'
  | 'pending_review'
  | 'verified'
  | 'rejected';

export function setIntercomProperties(input: {
  user?: UserResponse | null;
  teamDetails?: TeamResponse | null;
  userEhr?: EHRProfileResponse | null;
  userEhrLaunch?: UserEHRLaunchProfile | null;
  integrationOptions: IntegrationOptions | null;
  billingInfo?: UserBillingInfo | null;
  workspace?: JoinedWorkspaceRelation | null;
  locale?: string | null;
  isSessionPageV2Enabled?: boolean;
  isProjectsEnabled?: boolean;
  // Data-residency region as the identity/session claim states it (see
  // `useDatabaseRegion`) — not `getRegion()`, which honours a user-supplied
  // `?region=`. Left undefined while unknown so Intercom keeps the last value.
  databaseRegion?: Region;
  // Left undefined while unknown so Intercom keeps the last value.
  hcpVerificationStatus?: HcpVerificationIntercomStatus;
}) {
  // TELEMETRY-GATE: F1 function-entry gate. Canonical pattern from
  // .ai-context/onprem-guardrails.md — no Intercom Messenger egress to
  // api-iam.intercom.io or widget.intercom.io on a suppressed build.
  if (!isSaasTelemetryEnabled) return;

  const {
    user,
    teamDetails,
    userEhr,
    userEhrLaunch,
    integrationOptions,
    billingInfo,
    workspace,
    locale,
    isSessionPageV2Enabled,
    isProjectsEnabled,
    databaseRegion,
    hcpVerificationStatus,
  } = input;
  configureIntercomLocale(locale);
  if (isIntercomDisabledForLocale(locale)) return;

  const source = getAppType();

  const ehrProvider =
    userEhr?.ehr_profile?.ehr_provider ?? userEhrLaunch?.ehr_provider ?? null;

  const intercomSettings = {
    api_base: 'https://api-iam.intercom.io',
    app_id: IntercomId,
    name:
      user?.first_name || user?.last_name
        ? `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim()
        : null, // Full name or partial name if available
    user_id: user?.intercom_user_id || null, // a UUID for your user
    email: user?.email || null, // the email for your user
    clinician_type: user?.specialty_id, // The type of clinicians
    is_subscribed: user?.is_subscribed || teamDetails?.team.is_subscribed, // Whether user is on free or paid plan
    user_team_id: teamDetails?.team.id, // The team id of the user
    user_team_name: teamDetails?.team.name, // The team name of the user
    workspace_id: workspace?.workspace_id ?? null, // The active workspace id of the user
    workspace_name: workspace?.workspace_name ?? null, // The active workspace name of the user
    version: 'V2.0',
    ehr_provider: ehrProvider,
    has_integration: !!userEhr?.ehr_profile || !!userEhrLaunch,
    user_tier: user?.tier,
    is_heidi_labs: !!user?.session_settings?.opted_in_experimental,
    team_tier: teamDetails?.team.tier,
    effective_tier: billingInfo?.tier,
    // Which Stripe account the billing owner transacts on, so support can tell
    // which Stripe dashboard holds this customer. Left undefined (not null)
    // while billing info is still loading, so Intercom keeps the last known
    // value instead of blanking it on every boot before the query resolves.
    stripe_account_region: billingInfo?.stripe_account_region,
    source: (integrationOptions && integrationOptions.ehrName) || source,
    // Which session-page layout the user is effectively on (flag reach ×
    // Heidi Labs opt-in), so support can tell V1 from V2. The hook resolves to
    // a strict boolean — `false` while the flags/opt-in are still loading — so
    // a V2 user is briefly reported as 'v1' on the first boot; the identify
    // effect re-runs when the flag settles (it's in the effect dep arrays) and
    // corrects to 'v2'.
    layout_version: isSessionPageV2Enabled ? 'v2' : 'v1',
    // Resolved from release-scribe-enable-projects (desktop) or
    // release-scribe-enable-projects-web (web) via useIsProjectsEnabled.
    is_heidi_work_enabled: !!isProjectsEnabled,
    hide_default_launcher: true,
    user_hash: user?.intercom_user_hash || null,
    language_override: locale || undefined,
    'region-db': databaseRegion,
    hcp_verification_status: hcpVerificationStatus,
  };

  // Ensure Intercom is loaded and booted/updated with the latest settings
  bootOrUpdate(intercomSettings as IntercomTypes.IntercomSettings);
}
